import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Stage, Layer, Rect, Text, Transformer, Image, Circle, Ellipse } from 'react-konva';
import Konva from 'konva';
import { templateService, fontService } from '../../services/api';
import { Template, CanvasElement } from '../../types';
import { useAuth } from '../../context/AuthContext';

interface KonvaElement {
  id: string;
  type: 'text' | 'image' | 'rectangle' | 'circle';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  zIndex?: number; // Layer order - higher numbers appear on top
  // Text properties
  text?: string;
  fontSize?: number;
  fontFamily?: string;
  fill?: string;
  textAlign?: string;
  verticalAlign?: string; // New: vertical alignment for text
  fontWeight?: string;
  // Image properties
  imageUrl?: string;
  opacity?: number;
  // Shape properties
  stroke?: string;
  strokeWidth?: number;
  cornerRadius?: number; // for rectangle
  // Common properties
  draggable: boolean;
  locked?: boolean; // New: lock element to prevent selection and editing
  // Dynamic properties mapping - each property can have its own dynamic key
  dynamicProps?: {
    [key: string]: string; // property name -> data key mapping
  };
}

// Property input component with dynamic toggle - moved outside to prevent re-creation
const PropertyInput = React.memo(({ 
  label, 
  propertyName, 
  value, 
  type = 'text', 
  element, 
  onChange, 
  options,
  isLoading = false,
  onUpdateDynamicProperty,
  isPropertyDynamic,
  getPropertyDataKey
}: {
  label: string;
  propertyName: string;
  value: any;
  type?: 'text' | 'number' | 'color' | 'select' | 'range';
  element: KonvaElement;
  onChange: (value: any) => void;
  options?: { value: string; label: string; disabled?: boolean }[];
  isLoading?: boolean;
  onUpdateDynamicProperty: (elementId: string, propertyName: string, dataKey: string, forceDelete?: boolean) => void;
  isPropertyDynamic: (element: KonvaElement, propertyName: string) => boolean;
  getPropertyDataKey: (element: KonvaElement, propertyName: string) => string;
}) => {
  const isDynamic = isPropertyDynamic(element, propertyName);
  const dataKey = getPropertyDataKey(element, propertyName);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <button
          onClick={() => {
            if (isDynamic) {
              onUpdateDynamicProperty(element.id, propertyName, '', true); // Force delete when toggling off
            } else {
              onUpdateDynamicProperty(element.id, propertyName, propertyName, false); // Set default value when toggling on
            }
          }}
          className={`px-2 py-1 text-xs rounded transition-colors ${
            isDynamic 
              ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {isDynamic ? '🔗 Dynamic' : '📌 Static'}
        </button>
      </div>
      
      {isDynamic ? (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
          <label className="block text-xs font-medium text-purple-700 mb-1">Data Key</label>
          <input
            type="text"
            value={dataKey}
            onChange={(e) => onUpdateDynamicProperty(element.id, propertyName, e.target.value, false)}
            placeholder={`e.g. ${propertyName}`}
            className="w-full px-2 py-1 border border-purple-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <div className="text-xs text-purple-600 mt-1">
            This property will be replaced with dynamic data
          </div>
        </div>
      ) : (
        <div>
          {type === 'select' ? (
            <select
              value={value}
              onChange={(e) => onChange(e.target.value)}
              disabled={isLoading}
              className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <option>Loading fonts...</option>
              ) : (
                options?.map((option, index) => (
                  <option 
                    key={option.value || index} 
                    value={option.value} 
                    disabled={option.disabled}
                    style={option.disabled ? { fontWeight: 'bold', color: '#666' } : {}}
                  >
                    {option.label}
                  </option>
                ))
              )}
            </select>
          ) : type === 'range' ? (
            <div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                className="w-full"
              />
              <div className="text-xs text-gray-500 text-center">{Math.round(value * 100)}%</div>
            </div>
          ) : propertyName === 'text' ? (
            <textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              rows={2}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
            />
          ) : (
            <input
              type={type}
              value={value}
              onChange={(e) => onChange(type === 'number' ? (parseInt(e.target.value) || 0) : e.target.value)}
              className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 ${
                type === 'color' ? 'h-8 cursor-pointer' : ''
              }`}
            />
          )}
        </div>
      )}
    </div>
  );
});

const KonvaCanvasEditor: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const { apiKey } = useAuth();
  
  // State
  const [template, setTemplate] = useState<Template | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [templateName, setTemplateName] = useState('New Template');
  const [canvasSize, setCanvasSize] = useState({ width: 1080, height: 1080 });
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [elements, setElements] = useState<KonvaElement[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showApiPopup, setShowApiPopup] = useState(false);
  const [testInputs, setTestInputs] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<string | null>(null);
  const [isTestingApi, setIsTestingApi] = useState(false);
  const [availableFonts, setAvailableFonts] = useState<{
    system: string[];
    google: string[];
    categories: { 'sans-serif': string[]; serif: string[]; monospace: string[]; };
  } | null>(null);
  const [isLoadingFonts, setIsLoadingFonts] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const [copiedElement, setCopiedElement] = useState<KonvaElement | null>(null);
  const [canvasFit, setCanvasFit] = useState({
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    paddingLeft: 0,
    paddingTop: 0,
  });
  
  // Undo/Redo state
  const [history, setHistory] = useState<KonvaElement[][]>([]);
  const [historyStep, setHistoryStep] = useState(-1);
  
  // Refs
  const stageRef = useRef<Konva.Stage>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  const updateCanvasFit = useCallback(() => {
    if (!canvasContainerRef.current) return;

    const container = canvasContainerRef.current;
    const computedStyle = window.getComputedStyle(container);
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

    const availableWidth = Math.max(container.clientWidth - (paddingLeft + paddingRight), 0);
    const availableHeight = Math.max(container.clientHeight - (paddingTop + paddingBottom), 0);

    if (canvasSize.width === 0 || canvasSize.height === 0 || availableWidth === 0 || availableHeight === 0) {
      setCanvasFit(prev => ({
        scale: 1,
        offsetX: 0,
        offsetY: 0,
        paddingLeft: prev.paddingLeft,
        paddingTop: prev.paddingTop,
      }));
      return;
    }

    const widthScale = availableWidth / canvasSize.width;
    const heightScale = availableHeight / canvasSize.height;
    const nextScale = Math.min(widthScale, heightScale, 1);
    const scaledWidth = canvasSize.width * nextScale;
    const scaledHeight = canvasSize.height * nextScale;

    setCanvasFit({
      scale: Number.isFinite(nextScale) && nextScale > 0 ? nextScale : 1,
      offsetX: Math.max((availableWidth - scaledWidth) / 2, 0),
      offsetY: Math.max((availableHeight - scaledHeight) / 2, 0),
      paddingLeft,
      paddingTop,
    });
  }, [canvasSize]);

  // Load fonts
  const loadFonts = useCallback(async () => {
    if (availableFonts) return; // Already loaded
    
    setIsLoadingFonts(true);
    try {
      console.log('📝 Loading available fonts...');
      const fontsData = await fontService.getFonts();
      setAvailableFonts(fontsData.fonts);
      console.log('✅ Fonts loaded:', fontsData.fonts);
    } catch (error) {
      console.error('❌ Error loading fonts:', error);
      // Fallback to basic fonts
      setAvailableFonts({
        system: ['Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana'],
        google: [],
        categories: {
          'sans-serif': ['Arial', 'Helvetica', 'Verdana'],
          serif: ['Times New Roman', 'Georgia'],
          monospace: ['Courier New', 'Monaco']
        }
      });
    } finally {
      setIsLoadingFonts(false);
    }
  }, [availableFonts]);

  // Load template
  const loadTemplate = useCallback(async () => {
    if (!templateId) {
      console.log('🆕 No template ID - creating new template');
      setIsLoading(false);
      return;
    }

    console.log('📥 Loading template with Konva:', templateId);
    setIsLoading(true);
    
    try {
      const templateData = await templateService.getTemplate(templateId);
      console.log('📋 Template data received:', templateData);
      
      setTemplate(templateData);
      setTemplateName(templateData.name);
      setCanvasSize({ width: templateData.width, height: templateData.height });
      setBackgroundColor(templateData.backgroundColor);

      // Convert template elements to Konva elements
      const konvaElements: KonvaElement[] = templateData.elements.map((element: CanvasElement) => ({
        id: element.id,
        type: element.type,
        x: element.x,
        y: element.y,
        width: element.width,
        height: element.height,
        rotation: element.rotation || 0,
        zIndex: element.zIndex || 0,
        // Text properties
        text: element.text || 'Sample Text',
        fontSize: element.fontSize || 24,
        fontFamily: element.fontFamily || 'Arial',
        fill: element.color || '#000000',
        textAlign: element.textAlign || 'left',
        fontWeight: element.fontWeight || 'normal',
        verticalAlign: element.verticalAlign || 'top',
        // Image properties
        imageUrl: element.imageUrl || '',
        opacity: element.opacity || 1,
        // Common properties
        draggable: true,
        // Dynamic properties - convert old system to new system if needed
        dynamicProps: (element as any).dynamicProps || ((element as any).isDynamic && (element as any).dataKey ? { [element.type === 'text' ? 'text' : 'imageUrl']: (element as any).dataKey } : {})
      }));

      setElements(konvaElements);
      // Initialize history with loaded elements
      setHistory([JSON.parse(JSON.stringify(konvaElements))]);
      setHistoryStep(0);
      console.log('✅ Template loaded with Konva:', konvaElements.length, 'elements');
      setIsLoading(false);
      
    } catch (error) {
      console.error('❌ Error loading template:', error);
      setIsLoading(false);
    }
  }, [templateId]);

  // Save template
  const saveTemplate = useCallback(async () => {
    setIsSaving(true);
    try {
      const templateElements: CanvasElement[] = elements.map((element) => ({
        id: element.id,
        type: element.type,
        x: element.x,
        y: element.y,
        width: element.width,
        height: element.height,
        rotation: element.rotation || 0,
        zIndex: element.zIndex || 0,
        // Text properties
        text: element.text || '',
        fontSize: element.fontSize || 24,
        fontFamily: element.fontFamily || 'Arial',
        color: element.fill || '#000000',
        backgroundColor: 'transparent',
        textAlign: element.textAlign || 'left',
        verticalAlign: element.verticalAlign || 'top',
        fontWeight: element.fontWeight || 'normal',
        // Image properties
        imageUrl: element.imageUrl || '',
        opacity: element.opacity || 1,
        // Shape properties
        fill: element.fill || '#000000',
        stroke: element.stroke || '#000000',
        strokeWidth: element.strokeWidth || 1,
        cornerRadius: element.cornerRadius || 0,
        // Dynamic properties
        dynamicProps: element.dynamicProps || {}
      }));

      const templateData = {
        name: templateName,
        width: canvasSize.width,
        height: canvasSize.height,
        backgroundColor: backgroundColor,
        elements: templateElements,
      };

      if (template && templateId) {
        // Update existing template
        await templateService.updateTemplate(template._id, templateData);
        console.log('✅ Template updated with Konva');
      } else {
        // Create new template
        const response = await templateService.createTemplate(templateData);
        console.log('✅ New template created with Konva:', response);
        
        // Redirect to edit mode
        window.location.href = `/editor/${response.template._id}`;
      }
      
    } catch (error) {
      console.error('❌ Error saving template:', error);
    } finally {
      setIsSaving(false);
    }
  }, [elements, templateName, canvasSize, backgroundColor, template, templateId]);

  // Auto-save function
  const autoSave = useCallback(async () => {
    if (!hasUnsavedChanges || !templateId || isSaving) return;
    
    try {
      console.log('💾 Auto-saving template...');
      await saveTemplate();
      setHasUnsavedChanges(false);
      setLastAutoSave(new Date());
      console.log('✅ Auto-save completed');
    } catch (error) {
      console.error('❌ Auto-save failed:', error);
    }
  }, [hasUnsavedChanges, templateId, isSaving, saveTemplate]);

  // Mark changes as unsaved
  const markUnsaved = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  // Add to history for undo/redo
  const addToHistory = useCallback((newElements: KonvaElement[]) => {
    setHistory(prev => {
      // Remove any history after current step
      const newHistory = prev.slice(0, historyStep + 1);
      // Add new state
      newHistory.push(JSON.parse(JSON.stringify(newElements)));
      // Keep only last 50 steps
      if (newHistory.length > 50) {
        newHistory.shift();
        return newHistory;
      }
      return newHistory;
    });
    setHistoryStep(prev => Math.min(prev + 1, 49));
  }, [historyStep]);

  // Undo function
  const undo = useCallback(() => {
    if (historyStep > 0) {
      const previousStep = historyStep - 1;
      setHistoryStep(previousStep);
      setElements(JSON.parse(JSON.stringify(history[previousStep])));
      setSelectedId(null);
      markUnsaved();
      console.log('⬅️ Undo to step:', previousStep);
    }
  }, [historyStep, history, markUnsaved]);

  // Redo function
  const redo = useCallback(() => {
    if (historyStep < history.length - 1) {
      const nextStep = historyStep + 1;
      setHistoryStep(nextStep);
      setElements(JSON.parse(JSON.stringify(history[nextStep])));
      setSelectedId(null);
      markUnsaved();
      console.log('➡️ Redo to step:', nextStep);
    }
  }, [historyStep, history, markUnsaved]);

  // Handle element selection
  const handleSelect = (id: string) => {
    const element = elements.find(el => el.id === id);
    // Don't select locked elements
    if (element && !element.locked) {
      setSelectedId(id);
    }
  };

  const handleDeselect = () => {
    setSelectedId(null);
  };

  // Toggle element lock
  const toggleLock = (id: string) => {
    setElements(prev => prev.map(el => 
      el.id === id ? { ...el, locked: !el.locked } : el
    ));
    // Deselect if locking
    const element = elements.find(el => el.id === id);
    if (element && !element.locked && selectedId === id) {
      setSelectedId(null);
    }
    markUnsaved();
    console.log('🔒 Toggled lock for element:', id);
  };

  // Get next zIndex for new elements
  const getNextZIndex = () => {
    const maxZIndex = Math.max(...elements.map(el => el.zIndex || 0), 0);
    return maxZIndex + 1;
  };

  // Handle element drag
  const handleDragEnd = (id: string, e: any) => {
    const element = elements.find(el => el.id === id);
    if (element?.type === 'circle') {
      // For circles, convert from center coordinates to top-left coordinates
      // e.target.x() and e.target.y() are the center coordinates from Ellipse
      setElements(prev => prev.map(el => 
        el.id === id 
          ? { 
              ...el, 
              x: e.target.x() - el.width / 2, 
              y: e.target.y() - el.height / 2 
            }
          : el
      ));
    } else {
      // For other elements, use coordinates directly
      setElements(prev => prev.map(el => 
        el.id === id 
          ? { ...el, x: e.target.x(), y: e.target.y() }
          : el
      ));
    }
    markUnsaved(); // Mark as unsaved for auto-save
    console.log('🎯 Element dragged:', id, 'to', e.target.x(), e.target.y());
  };

  // Handle element transform
  const handleTransformEnd = (id: string, e: any) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const element = elements.find(el => el.id === id);

    if (element?.type === 'circle') {
      // For circles, convert from center coordinates to top-left coordinates
      setElements(prev => prev.map(el => 
        el.id === id 
          ? { 
              ...el, 
              x: node.x() - (Math.max(5, node.width() * scaleX)) / 2,
              y: node.y() - (Math.max(5, node.height() * scaleY)) / 2,
              width: Math.max(5, node.width() * scaleX),
              height: Math.max(5, node.height() * scaleY)
            }
          : el
      ));
    } else {
      setElements(prev => prev.map(el => 
        el.id === id 
          ? { 
              ...el, 
              x: node.x(),
              y: node.y(),
              width: Math.max(5, node.width() * scaleX),
              height: Math.max(5, node.height() * scaleY)
            }
          : el
      ));
    }

    // Reset scale to prevent accumulation
    node.scaleX(1);
    node.scaleY(1);
    
    // Force transformer to update with new dimensions
    if (transformerRef.current) {
      transformerRef.current.forceUpdate();
      transformerRef.current.getLayer()?.batchDraw();
    }
    
    markUnsaved(); // Mark as unsaved for auto-save
    console.log('🔄 Element transformed:', id, 'New size:', Math.max(5, node.width() * scaleX), 'x', Math.max(5, node.height() * scaleY));
  };

  // Drag & Drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    if (!canvasContainerRef.current) return;

    const elementType = e.dataTransfer.getData('text/plain');
    const rect = canvasContainerRef.current.getBoundingClientRect();
    const scale = canvasFit.scale || 1;
    const adjustedX = (e.clientX - rect.left - canvasFit.paddingLeft - canvasFit.offsetX) / scale;
    const adjustedY = (e.clientY - rect.top - canvasFit.paddingTop - canvasFit.offsetY) / scale;
    const x = Math.max(0, adjustedX);
    const y = Math.max(0, adjustedY);

    console.log('🎯 Konva Drop:', { elementType, x, y });

    if (elementType === 'text') {
      addTextAt(x, y);
    } else if (elementType === 'image') {
      addImageAt(x, y);
    } else if (elementType === 'rectangle') {
      addRectangleAt(x, y);
    } else if (elementType === 'circle') {
      addCircleAt(x, y);
    }
  };

  const addTextAt = (x: number, y: number) => {
    const newElement: KonvaElement = {
      id: `text-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'text',
      x: Math.max(0, x),
      y: Math.max(0, y),
      width: 200,
      height: 30,
      rotation: 0,
      zIndex: getNextZIndex(),
      text: 'New Text',
      fontSize: 24,
      fontFamily: 'Arial',
      fill: '#000000',
      textAlign: 'left',
      verticalAlign: 'top',
      fontWeight: 'normal',
      draggable: true,
      locked: false,
      dynamicProps: {}
    };

    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedId(newElement.id);
    markUnsaved();
    console.log('✅ Konva text added with ID:', newElement.id, 'zIndex:', newElement.zIndex);
  };

  const addImageAt = (x: number, y: number) => {
    const newElement: KonvaElement = {
      id: `image-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      x: Math.max(0, x),
      y: Math.max(0, y),
      width: 200,
      height: 150,
      rotation: 0,
      zIndex: getNextZIndex(),
      imageUrl: '',
      opacity: 1,
      fill: '#f3f4f6',
      draggable: true,
      locked: false,
      dynamicProps: {}
    };

    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedId(newElement.id);
    markUnsaved();
    console.log('✅ Konva image added with ID:', newElement.id, 'zIndex:', newElement.zIndex);
  };

  const addRectangleAt = (x: number, y: number) => {
    const newElement: KonvaElement = {
      id: `rectangle-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'rectangle',
      x: Math.max(0, x),
      y: Math.max(0, y),
      width: 150,
      height: 100,
      rotation: 0,
      zIndex: getNextZIndex(),
      fill: '#3b82f6',
      stroke: '#1e40af',
      strokeWidth: 2,
      cornerRadius: 8,
      opacity: 1,
      draggable: true,
      locked: false,
      dynamicProps: {}
    };

    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedId(newElement.id);
    markUnsaved();
    console.log('✅ Konva rectangle added with ID:', newElement.id, 'zIndex:', newElement.zIndex);
  };

  const addCircleAt = (x: number, y: number) => {
    const newElement: KonvaElement = {
      id: `circle-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'circle',
      x: Math.max(0, x),
      y: Math.max(0, y),
      width: 120,
      height: 120,
      rotation: 0,
      zIndex: getNextZIndex(),
      fill: '#10b981',
      stroke: '#059669',
      strokeWidth: 2,
      opacity: 1,
      draggable: true,
      locked: false,
      dynamicProps: {}
    };

    const newElements = [...elements, newElement];
    setElements(newElements);
    addToHistory(newElements);
    setSelectedId(newElement.id);
    markUnsaved();
    console.log('✅ Konva circle added with ID:', newElement.id, 'zIndex:', newElement.zIndex);
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    const newElements = elements.filter(el => el.id !== selectedId);
    setElements(newElements);
    addToHistory(newElements);
    setSelectedId(null);
    markUnsaved();
  };

  // Layer management functions
  const bringToFront = (id: string) => {
    const maxZIndex = Math.max(...elements.map(el => el.zIndex || 0));
    setElements(prev => prev.map(el => 
      el.id === id ? { ...el, zIndex: maxZIndex + 1 } : el
    ));
    markUnsaved();
    console.log('📤 Brought element to front:', id);
  };

  const sendToBack = (id: string) => {
    const minZIndex = Math.min(...elements.map(el => el.zIndex || 0));
    setElements(prev => prev.map(el => 
      el.id === id ? { ...el, zIndex: Math.min(0, minZIndex - 1) } : el
    ));
    markUnsaved();
    console.log('📥 Sent element to back:', id);
  };

  const bringForward = (id: string) => {
    const element = elements.find(el => el.id === id);
    if (!element) return;
    
    const currentZIndex = element.zIndex || 0;
    const higherElements = elements.filter(el => (el.zIndex || 0) > currentZIndex);
    
    if (higherElements.length > 0) {
      const nextZIndex = Math.min(...higherElements.map(el => el.zIndex || 0));
      setElements(prev => prev.map(el => 
        el.id === id ? { ...el, zIndex: nextZIndex + 0.5 } : el
      ));
      markUnsaved();
      console.log('⬆️ Brought element forward:', id);
    }
  };

  const sendBackward = (id: string) => {
    const element = elements.find(el => el.id === id);
    if (!element) return;
    
    const currentZIndex = element.zIndex || 0;
    const lowerElements = elements.filter(el => (el.zIndex || 0) < currentZIndex);
    
    if (lowerElements.length > 0) {
      const prevZIndex = Math.max(...lowerElements.map(el => el.zIndex || 0));
      setElements(prev => prev.map(el => 
        el.id === id ? { ...el, zIndex: prevZIndex - 0.5 } : el
      ));
      markUnsaved();
      console.log('⬇️ Sent element backward:', id);
    }
  };

  // Copy-paste functions
  const copyElement = (id: string) => {
    const element = elements.find(el => el.id === id);
    if (element) {
      setCopiedElement(element);
      console.log('📋 Element copied:', id);
    }
  };

  const pasteElement = () => {
    if (!copiedElement) return;

    // Create a new element with same properties but new ID and position
    const newElement: KonvaElement = {
      ...copiedElement,
      id: `${copiedElement.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      x: copiedElement.x + 20, // Offset to avoid exact overlap
      y: copiedElement.y + 20,
      zIndex: getNextZIndex(),
      // Reset dynamic properties to avoid conflicts
      dynamicProps: {}
    };

    setElements(prev => [...prev, newElement]);
    setSelectedId(newElement.id);
    markUnsaved();
    console.log('📌 Element pasted:', newElement.id, 'from', copiedElement.id);
  };

  const duplicateElement = (id: string) => {
    const element = elements.find(el => el.id === id);
    if (!element) return;

    // Create a duplicate with same properties but new ID and offset position
    const newElement: KonvaElement = {
      ...element,
      id: `${element.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      x: element.x + 20, // Offset to avoid exact overlap
      y: element.y + 20,
      zIndex: getNextZIndex(),
      // Reset dynamic properties to avoid conflicts
      dynamicProps: {}
    };

    setElements(prev => [...prev, newElement]);
    setSelectedId(newElement.id);
    markUnsaved();
    console.log('🔄 Element duplicated:', newElement.id, 'from', id);
  };

  // Update element properties
  const updateElementProperty = (id: string, property: string, value: any) => {
    setElements(prev => prev.map(el => 
      el.id === id ? { ...el, [property]: value } : el
    ));
    
    // If we're updating size/position properties, force transformer update
    if (['width', 'height', 'x', 'y'].includes(property) && selectedId === id) {
      setTimeout(() => {
        if (transformerRef.current && stageRef.current) {
          const selectedNode = stageRef.current.findOne(`#${id}`);
          if (selectedNode) {
            transformerRef.current.forceUpdate();
            transformerRef.current.getLayer()?.batchDraw();
          }
        }
      }, 0);
    }
    
    markUnsaved(); // Mark as unsaved for auto-save
    console.log('🔧 Element property updated:', id, property, value);
  };

  // Update dynamic properties
  const updateDynamicProperty = (elementId: string, propertyName: string, dataKey: string, forceDelete = false) => {
    setElements(prev => prev.map(el => {
      if (el.id === elementId) {
        const newDynamicProps = { ...el.dynamicProps };
        if (forceDelete || (dataKey === '' && forceDelete)) {
          // Only delete the property when explicitly requested (toggle button)
          delete newDynamicProps[propertyName];
        } else {
          // Keep the property even if empty string (user is typing)
          newDynamicProps[propertyName] = dataKey;
        }
        return { ...el, dynamicProps: newDynamicProps };
      }
      return el;
    }));
    markUnsaved(); // Mark as unsaved for auto-save
  };

  // Check if a property is dynamic
  const isPropertyDynamic = (element: KonvaElement, propertyName: string) => {
    return !!(element.dynamicProps && element.dynamicProps.hasOwnProperty(propertyName));
  };

  // Get data key for a property
  const getPropertyDataKey = (element: KonvaElement, propertyName: string) => {
    return element.dynamicProps?.[propertyName] || '';
  };

  // Get selected element
  const selectedElement = elements.find(el => el.id === selectedId);

  // Get dynamic variables from template
  const getDynamicVariables = () => {
    const allKeys: string[] = [];
    elements.forEach(el => {
      if (el.dynamicProps) {
        Object.values(el.dynamicProps).forEach(key => {
          if (key && !allKeys.includes(key)) {
            allKeys.push(key);
          }
        });
      }
    });
    return allKeys;
  };

  // Copy to clipboard function
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Test API function
  const testApi = async (endpoint: string) => {
    if (!templateId) {
      alert('Please save the template first!');
      return;
    }

    setIsTestingApi(true);
    setTestResult(null);

    try {
      console.log('🔄 Testing API with JWT token authentication');
      
      const token = localStorage.getItem('token');
      const baseUrl = window.location.origin.replace(':3000', ':5001'); // Use current domain for testing
      
      console.log('🔄 Testing API:', `${baseUrl}/api/render/template${endpoint}`);
      console.log('📤 Request data:', { templateId, data: testInputs });
      
      const response = await fetch(`${baseUrl}/api/render/template${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          templateId,
          data: testInputs
        })
      });

      console.log('📥 Response status:', response.status);
      console.log('📥 Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      if (endpoint === '/base64') {
        const result = await response.json();
        console.log('✅ Base64 result:', result);
        setTestResult(`Success! Base64 image generated. Length: ${result.image.length} characters`);
        
        // Show base64 image in new tab
        const newWindow = window.open();
        if (newWindow) {
          newWindow.document.write(`
            <html>
              <head><title>Base64 Test Result</title></head>
              <body style="margin: 0; padding: 20px; background: #f0f0f0;">
                <h2>Base64 Test Result</h2>
                <img src="${result.image}" style="max-width: 100%; height: auto; border: 1px solid #ccc;" />
                <div style="margin-top: 20px; font-family: monospace; font-size: 12px; background: white; padding: 10px; border-radius: 5px;">
                  <strong>Template ID:</strong> ${result.templateId}<br>
                  <strong>Render Count:</strong> ${result.renderCount}<br>
                  <strong>Image Length:</strong> ${result.image.length} characters
                </div>
              </body>
            </html>
          `);
        }
      } else {
        const blob = await response.blob();
        console.log('✅ PNG blob:', blob);
        
        if (blob.size === 0) {
          throw new Error('Received empty image blob');
        }
        
        const imageUrl = URL.createObjectURL(blob);
        setTestResult(`Success! PNG image generated. Size: ${blob.size} bytes`);
        
        // Show PNG image in new tab
        const newWindow = window.open();
        if (newWindow) {
          newWindow.document.write(`
            <html>
              <head><title>PNG Test Result</title></head>
              <body style="margin: 0; padding: 20px; background: #f0f0f0;">
                <h2>PNG Test Result</h2>
                <img src="${imageUrl}" style="max-width: 100%; height: auto; border: 1px solid #ccc;" />
                <div style="margin-top: 20px; font-family: monospace; font-size: 12px; background: white; padding: 10px; border-radius: 5px;">
                  <strong>Template ID:</strong> ${templateId}<br>
                  <strong>Image Size:</strong> ${blob.size} bytes<br>
                  <strong>Content Type:</strong> ${blob.type}
                </div>
                <div style="margin-top: 10px;">
                  <a href="${imageUrl}" download="test-image.png" style="background: #007bff; color: white; padding: 8px 16px; text-decoration: none; border-radius: 4px;">Download PNG</a>
                </div>
              </body>
            </html>
          `);
        }
      }
    } catch (error) {
      console.error('❌ API Test Error:', error);
      setTestResult(`Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsTestingApi(false);
    }
  };

  // Generate curl commands with API key
  const generateCurlCommand = (endpoint: string) => {
    const baseUrl = 'https://api-postify.eymel.co/api'; // Production API URL for curl examples
    const requestData = {
      templateId: templateId || 'TEMPLATE_ID',
      data: Object.keys(testInputs).length > 0 ? testInputs : {}
    };
    const dataJson = JSON.stringify(requestData, null, 2);
    
    const outputFlag = endpoint === '' ? ' \\\n  --output image.png' : '';
    const actualApiKey = apiKey || 'YOUR_API_KEY';
    
    return `curl -X POST "${baseUrl}/render/template${endpoint}" \\\n  -H "Content-Type: application/json" \\\n  -H "x-api-key: ${actualApiKey}" \\\n  -d '${dataJson}'${outputFlag}`;
  };

  // Image component for Konva with aspect ratio preservation
  const KonvaImageComponent = ({ element }: { element: KonvaElement }) => {
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [scaledDimensions, setScaledDimensions] = useState({ width: element.width, height: element.height, x: 0, y: 0 });

    useEffect(() => {
      if (element.imageUrl && element.imageUrl.trim()) {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          setImage(img);
          
          // Calculate scaled dimensions to fit within boundaries while preserving aspect ratio
          const containerWidth = element.width;
          const containerHeight = element.height;
          const imageAspectRatio = img.width / img.height;
          const containerAspectRatio = containerWidth / containerHeight;
          
          let scaledWidth, scaledHeight, offsetX = 0, offsetY = 0;
          
          if (imageAspectRatio > containerAspectRatio) {
            // Image is wider - fit to width
            scaledWidth = containerWidth;
            scaledHeight = containerWidth / imageAspectRatio;
            offsetY = (containerHeight - scaledHeight) / 2;
          } else {
            // Image is taller - fit to height
            scaledHeight = containerHeight;
            scaledWidth = containerHeight * imageAspectRatio;
            offsetX = (containerWidth - scaledWidth) / 2;
          }
          
          setScaledDimensions({
            width: scaledWidth,
            height: scaledHeight,
            x: offsetX,
            y: offsetY
          });
        };
        img.onerror = () => {
          console.error('Failed to load image:', element.imageUrl);
          setImage(null);
        };
        img.src = element.imageUrl;
      } else {
        setImage(null);
      }
    }, [element.imageUrl, element.width, element.height]);

    if (image) {
      return (
        <Image
          id={element.id}
          x={element.x}
          y={element.y}
          width={element.width}
          height={element.height}
          image={image}
          opacity={element.locked ? 0.6 : (element.opacity || 1)}
          draggable={element.draggable && !element.locked}
          onClick={() => handleSelect(element.id)}
          onTap={() => handleSelect(element.id)}
          onDragEnd={(e: any) => handleDragEnd(element.id, e)}
          onTransformEnd={(e: any) => handleTransformEnd(element.id, e)}
        />
      );
    } else {
      // Placeholder rectangle
      return (
        <Rect
          id={element.id}
          x={element.x}
          y={element.y}
          width={element.width}
          height={element.height}
          fill={element.fill || '#f3f4f6'}
          stroke="#d1d5db"
          strokeWidth={2}
          dash={[5, 5]}
          cornerRadius={8}
          opacity={element.locked ? 0.6 : (element.opacity || 1)}
          draggable={element.draggable && !element.locked}
          onClick={() => handleSelect(element.id)}
          onTap={() => handleSelect(element.id)}
          onDragEnd={(e: any) => handleDragEnd(element.id, e)}
          onTransformEnd={(e: any) => handleTransformEnd(element.id, e)}
        />
      );
    }
  };

  // Update transformer when selection changes or elements update
  useEffect(() => {
    if (selectedId && transformerRef.current && stageRef.current) {
      const selectedNode = stageRef.current.findOne(`#${selectedId}`);
      if (selectedNode) {
        // Ensure the node has correct dimensions
        const element = elements.find(el => el.id === selectedId);
        if (element) {
          selectedNode.width(element.width);
          selectedNode.height(element.height);
          
          if (element.type === 'circle') {
            // For circles, set center coordinates
            selectedNode.x(element.x + element.width / 2);
            selectedNode.y(element.y + element.height / 2);
          } else {
            // For other elements, set top-left coordinates
            selectedNode.x(element.x);
            selectedNode.y(element.y);
          }
        }
        
        transformerRef.current.nodes([selectedNode]);
        transformerRef.current.forceUpdate();
        transformerRef.current.getLayer()?.batchDraw();
      }
    } else if (transformerRef.current) {
      transformerRef.current.nodes([]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [selectedId, elements]);

  // Effects
  useEffect(() => {
    loadTemplate();
    loadFonts();
  }, [loadTemplate, loadFonts]);

  useEffect(() => {
    updateCanvasFit();

    const container = canvasContainerRef.current;
    const ResizeObserverClass = (window as any).ResizeObserver;

    if (!ResizeObserverClass || !container) {
      window.addEventListener('resize', updateCanvasFit);
      return () => {
        window.removeEventListener('resize', updateCanvasFit);
      };
    }

    const observer = new ResizeObserverClass(() => updateCanvasFit());
    observer.observe(container);
    window.addEventListener('resize', updateCanvasFit);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateCanvasFit);
    };
  }, [updateCanvasFit]);

  // Auto-save effect - save changes after 2 seconds of inactivity
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const autoSaveTimer = setTimeout(() => {
      autoSave();
    }, 2000); // 2 seconds delay

    return () => clearTimeout(autoSaveTimer);
  }, [hasUnsavedChanges, autoSave]);

  // Keyboard shortcuts effect
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle shortcuts when not typing in inputs
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Ctrl+Z or Cmd+Z - Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }

      // Ctrl+Shift+Z or Cmd+Shift+Z or Ctrl+Y - Redo
      if (((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') || (e.ctrlKey && e.key === 'y')) {
        e.preventDefault();
        redo();
        return;
      }

      // Ctrl+C or Cmd+C - Copy
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedId) {
        e.preventDefault();
        copyElement(selectedId);
      }

      // Ctrl+V or Cmd+V - Paste
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && copiedElement) {
        e.preventDefault();
        pasteElement();
      }

      // Ctrl+D or Cmd+D - Duplicate
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedId) {
        e.preventDefault();
        duplicateElement(selectedId);
      }

      // Ctrl+L or Cmd+L - Lock/Unlock selected element
      if ((e.ctrlKey || e.metaKey) && e.key === 'l' && selectedId) {
        e.preventDefault();
        toggleLock(selectedId);
      }

      // Delete or Backspace - Delete selected element
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault();
        deleteSelected();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, copiedElement, undo, redo]);

  // Handle font change with Google Font loading
  const handleFontChange = async (elementId: string, fontFamily: string) => {
    // Check if it's a Google Font that needs to be loaded
    if (availableFonts?.google.includes(fontFamily)) {
      try {
        console.log(`🔄 Loading Google Font: ${fontFamily}`);
        await fontService.loadGoogleFont(fontFamily);
        console.log(`✅ Google Font loaded: ${fontFamily}`);
      } catch (error) {
        console.error(`❌ Failed to load Google Font ${fontFamily}:`, error);
        // Continue anyway, fallback will be used
      }
    }
    
    // Update the element property
    updateElementProperty(elementId, 'fontFamily', fontFamily);
  };

  // Generate font options for select with categories
  const getFontOptions = () => {
    if (!availableFonts) {
      return [
        { value: 'Arial', label: 'Arial' },
        { value: 'Helvetica', label: 'Helvetica' },
        { value: 'Times New Roman', label: 'Times New Roman' },
        { value: 'Georgia', label: 'Georgia' },
        { value: 'Verdana', label: 'Verdana' }
      ];
    }

    const options: { value: string; label: string; category?: string; disabled?: boolean }[] = [];
    
    // Add category headers and popular fonts
    options.push({ value: '', label: '🌟 Popular Google Fonts', disabled: true });
    const popularGoogleFonts = ['Open Sans', 'Roboto', 'Lato', 'Montserrat', 'Poppins', 'Inter'];
    popularGoogleFonts.forEach(font => {
      if (availableFonts.google.includes(font)) {
        options.push({ value: font, label: `  ${font}`, category: 'google' });
      }
    });
    
    options.push({ value: '', label: '💻 System Fonts', disabled: true });
    const popularSystemFonts = ['Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana'];
    popularSystemFonts.forEach(font => {
      if (availableFonts.categories['sans-serif'].includes(font) || 
          availableFonts.categories.serif.includes(font)) {
        options.push({ value: font, label: `  ${font}`, category: 'system' });
      }
    });
    
    // Add more Google Fonts
    const remainingGoogleFonts = availableFonts.google.filter(font => !popularGoogleFonts.includes(font));
    if (remainingGoogleFonts.length > 0) {
      options.push({ value: '', label: '🔤 More Google Fonts', disabled: true });
      remainingGoogleFonts.forEach(font => {
        options.push({ value: font, label: `  ${font}`, category: 'google' });
      });
    }

    return options;
  };

  if (isLoading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-6"></div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {templateId ? 'Loading Template' : 'Initializing Editor'}
          </h3>
          <p className="text-gray-600">
            {templateId ? 'Loading your template...' : 'Setting up canvas...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-gray-900">
              🎨 {templateId ? 'Edit Template' : 'New Template'}
            </h1>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Template name"
            />
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-lg">
              Elements: {elements.length}
            </div>
            
            {/* Undo/Redo buttons */}
            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={undo}
                disabled={historyStep <= 0}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  historyStep > 0 
                    ? 'bg-white text-gray-700 hover:bg-gray-50' 
                    : 'text-gray-400 cursor-not-allowed'
                }`}
                title="Undo (Ctrl+Z)"
              >
                ↶ Undo
              </button>
              <button
                onClick={redo}
                disabled={historyStep >= history.length - 1}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  historyStep < history.length - 1
                    ? 'bg-white text-gray-700 hover:bg-gray-50' 
                    : 'text-gray-400 cursor-not-allowed'
                }`}
                title="Redo (Ctrl+Shift+Z)"
              >
                ↷ Redo
              </button>
            </div>

            <button
              onClick={() => setShowApiPopup(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
            >
              <span>🔗</span>
              <span>API Usage</span>
            </button>
            <button
              onClick={saveTemplate}
              disabled={isSaving}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
                hasUnsavedChanges 
                  ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                  : 'bg-green-600 hover:bg-green-700 text-white'
              } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSaving 
                ? '💾 Auto-saving...' 
                : hasUnsavedChanges 
                  ? (templateId ? '💾 Save Changes' : '💾 Create Template')
                  : '✅ Saved'
              }
            </button>
            {lastAutoSave && !hasUnsavedChanges && (
              <div className="text-xs text-gray-500 mt-1">
                Last saved: {lastAutoSave.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Left Sidebar */}
        <div className="w-80 bg-white border-r border-gray-200 shadow-sm">
          <div className="p-6 space-y-6">
            {/* Canvas Settings */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Canvas Settings</h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Width</label>
                    <input
                      type="number"
                      value={canvasSize.width}
                      onChange={(e) => setCanvasSize(prev => ({ ...prev, width: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Height</label>
                    <input
                      type="number"
                      value={canvasSize.height}
                      onChange={(e) => setCanvasSize(prev => ({ ...prev, height: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="w-full h-10 border border-gray-300 rounded-lg cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* Elements */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Elements</h3>
              
              <div className="space-y-3">
                <div
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('text/plain', 'text')}
                  className="flex items-center space-x-3 p-4 bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg cursor-grab hover:bg-blue-100 transition-colors"
                >
                  <span className="text-2xl">📝</span>
                  <div>
                    <div className="font-medium text-blue-900">Text</div>
                    <div className="text-sm text-blue-600">Drag to canvas</div>
                  </div>
                </div>

                <div
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('text/plain', 'image')}
                  className="flex items-center space-x-3 p-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg cursor-grab hover:bg-green-100 transition-colors"
                >
                  <span className="text-2xl">🖼️</span>
                  <div>
                    <div className="font-medium text-green-900">Image</div>
                    <div className="text-sm text-green-600">Drag to canvas</div>
                  </div>
                </div>

                <div
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('text/plain', 'rectangle')}
                  className="flex items-center space-x-3 p-4 bg-purple-50 border-2 border-dashed border-purple-300 rounded-lg cursor-grab hover:bg-purple-100 transition-colors"
                >
                  <span className="text-2xl">⬜</span>
                  <div>
                    <div className="font-medium text-purple-900">Rectangle</div>
                    <div className="text-sm text-purple-600">Drag to canvas</div>
                  </div>
                </div>

                <div
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('text/plain', 'circle')}
                  className="flex items-center space-x-3 p-4 bg-orange-50 border-2 border-dashed border-orange-300 rounded-lg cursor-grab hover:bg-orange-100 transition-colors"
                >
                  <span className="text-2xl">⭕</span>
                  <div>
                    <div className="font-medium text-orange-900">Circle</div>
                    <div className="text-sm text-orange-600">Drag to canvas</div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Canvas Area */}
        <div className={`flex-1 flex items-center justify-center p-8 ${selectedElement ? 'pr-4' : ''}`}>
          <div
            ref={canvasContainerRef}
            className={`relative bg-white rounded-xl shadow-lg p-6 transition-all duration-200 ${
              isDragOver ? 'ring-4 ring-blue-300 ring-opacity-50 scale-105' : ''
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              maxWidth: 'calc(100% - 2rem)',
              maxHeight: 'calc(100% - 2rem)',
              overflow: 'auto',
            }}
          >
            {/* Drag Over Overlay */}
            {isDragOver && (
              <div className="absolute inset-0 flex items-center justify-center bg-blue-50 bg-opacity-90 rounded-xl z-10 pointer-events-none">
                <div className="text-center">
                  <div className="text-6xl mb-4">🎯</div>
                  <div className="text-2xl font-bold text-blue-700 mb-2">Drop Here</div>
                  <div className="text-blue-600">Add element to canvas</div>
                </div>
              </div>
            )}
            
            <div className="canvas-wrapper" style={{ overflow: 'hidden' }}>
              <div
                style={{
                  paddingLeft: canvasFit.offsetX,
                  paddingRight: canvasFit.offsetX,
                  paddingTop: canvasFit.offsetY,
                  paddingBottom: canvasFit.offsetY,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '200px',
                }}
              >
                <div
                  style={{
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: backgroundColor,
                    position: 'relative',
                    overflow: 'hidden',
                    width: canvasSize.width * canvasFit.scale,
                    height: canvasSize.height * canvasFit.scale,
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      transformOrigin: 'top left',
                      transform: `scale(${canvasFit.scale})`,
                    }}
                  >
                    {React.createElement(Stage as any, {
                      ref: stageRef,
                      width: canvasSize.width,
                      height: canvasSize.height,
                      onMouseDown: (e: any) => {
                        // Deselect when clicking on empty area
                        if (e.target === e.target.getStage()) {
                          handleDeselect();
                        }
                      }
                    }, 
                      <Layer>
                        {/* Background */}
                        <Rect
                          width={canvasSize.width}
                          height={canvasSize.height}
                          fill={backgroundColor}
                        />
                        
                        {/* Elements - sorted by zIndex */}
                        {elements
                          .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
                          .map((element) => {
                          if (element.type === 'text') {
                            return (
                              <Text
                                key={element.id}
                                id={element.id}
                                x={element.x}
                                y={element.y}
                                width={element.width}
                                height={element.height}
                                text={element.text}
                                fontSize={element.fontSize}
                                fontFamily={element.fontFamily}
                                fill={element.fill}
                                align={element.textAlign}
                                verticalAlign={element.verticalAlign || 'top'}
                                fontStyle={element.fontWeight}
                                opacity={element.locked ? 0.6 : (element.opacity || 1)}
                                rotation={element.rotation || 0}
                                draggable={element.draggable && !element.locked}
                                onClick={() => handleSelect(element.id)}
                                onTap={() => handleSelect(element.id)}
                                onDragEnd={(e: any) => handleDragEnd(element.id, e)}
                                onTransformEnd={(e: any) => handleTransformEnd(element.id, e)}
                              />
                            );
                          } else if (element.type === 'rectangle') {
                            return (
                              <Rect
                                key={element.id}
                                id={element.id}
                                x={element.x}
                                y={element.y}
                                width={element.width}
                                height={element.height}
                                fill={element.fill}
                                stroke={element.stroke}
                                strokeWidth={element.strokeWidth || 0}
                                cornerRadius={element.cornerRadius || 0}
                                opacity={element.locked ? 0.6 : (element.opacity || 1)}
                                rotation={element.rotation || 0}
                                draggable={element.draggable && !element.locked}
                                onClick={() => handleSelect(element.id)}
                                onTap={() => handleSelect(element.id)}
                                onDragEnd={(e: any) => handleDragEnd(element.id, e)}
                                onTransformEnd={(e: any) => handleTransformEnd(element.id, e)}
                              />
                            );
                          } else if (element.type === 'circle') {
                            return (
                              <Ellipse
                                key={element.id}
                                id={element.id}
                                x={element.x + element.width / 2}
                                y={element.y + element.height / 2}
                                radiusX={element.width / 2}
                                radiusY={element.height / 2}
                                fill={element.fill}
                                stroke={element.stroke}
                                strokeWidth={element.strokeWidth || 0}
                                opacity={element.locked ? 0.6 : (element.opacity || 1)}
                                rotation={element.rotation || 0}
                                draggable={element.draggable && !element.locked}
                                onClick={() => handleSelect(element.id)}
                                onTap={() => handleSelect(element.id)}
                                onDragEnd={(e: any) => handleDragEnd(element.id, e)}
                                onTransformEnd={(e: any) => handleTransformEnd(element.id, e)}
                              />
                            );
                          } else if (element.type === 'image') {
                            return (
                              <KonvaImageComponent
                                key={element.id}
                                element={element}
                              />
                            );
                          }
                          return null;
                        })}
                        
                        {/* Transformer */}
                        <Transformer
                          ref={transformerRef}
                          boundBoxFunc={(oldBox: any, newBox: any) => {
                            // Limit resize
                            if (newBox.width < 5 || newBox.height < 5) {
                              return oldBox;
                            }
                            return newBox;
                          }}
                        />
                      </Layer>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Properties Panel */}
        <div className="w-80 bg-white border-l border-gray-200 shadow-sm">
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {selectedElement ? 'Element Properties' : 'Properties Panel'}
              </h3>
              
              {selectedElement ? (
                <div className="space-y-4">
                  <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                    ID: {selectedElement.id}
                  </div>

                  {/* Layer Controls */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <span className="mr-2">📚</span>
                      Layer Controls
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => selectedId && bringToFront(selectedId)}
                        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 rounded text-xs font-medium transition-colors"
                        title="Bring to Front"
                      >
                        📤 To Front
                      </button>
                      <button
                        onClick={() => selectedId && sendToBack(selectedId)}
                        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 rounded text-xs font-medium transition-colors"
                        title="Send to Back"
                      >
                        📥 To Back
                      </button>
                      <button
                        onClick={() => selectedId && bringForward(selectedId)}
                        className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-3 rounded text-xs font-medium transition-colors"
                        title="Bring Forward"
                      >
                        ⬆️ Forward
                      </button>
                      <button
                        onClick={() => selectedId && sendBackward(selectedId)}
                        className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-3 rounded text-xs font-medium transition-colors"
                        title="Send Backward"
                      >
                        ⬇️ Backward
                      </button>
                    </div>
                    
                    <div className="mt-2 text-xs text-gray-600 bg-gray-100 p-2 rounded">
                      Layer: {selectedElement?.zIndex || 0}
                    </div>
                  </div>

                  {/* Copy-Paste Controls */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <span className="mr-2">📋</span>
                      Copy & Paste
                    </h4>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => selectedId && copyElement(selectedId)}
                        className="bg-green-600 hover:bg-green-700 text-white py-2 px-2 rounded text-xs font-medium transition-colors"
                        title="Copy (Ctrl+C)"
                      >
                        📋 Copy
                      </button>
                      <button
                        onClick={pasteElement}
                        disabled={!copiedElement}
                        className={`py-2 px-2 rounded text-xs font-medium transition-colors ${
                          copiedElement 
                            ? 'bg-green-600 hover:bg-green-700 text-white' 
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                        title="Paste (Ctrl+V)"
                      >
                        📌 Paste
                      </button>
                      <button
                        onClick={() => selectedId && duplicateElement(selectedId)}
                        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-2 rounded text-xs font-medium transition-colors"
                        title="Duplicate (Ctrl+D)"
                      >
                        🔄 Duplicate
                      </button>
                    </div>
                    
                    {copiedElement && (
                      <div className="mt-2 text-xs text-gray-600 bg-gray-100 p-2 rounded">
                        Copied: {copiedElement.type} ({copiedElement.id.split('-')[0]})
                      </div>
                    )}
                  </div>

                  {/* Common Properties */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">X</label>
                      <input
                        type="number"
                        value={Math.round(selectedElement.x)}
                        onChange={(e) => updateElementProperty(selectedElement.id, 'x', parseInt(e.target.value) || 0)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Y</label>
                      <input
                        type="number"
                        value={Math.round(selectedElement.y)}
                        onChange={(e) => updateElementProperty(selectedElement.id, 'y', parseInt(e.target.value) || 0)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Width</label>
                      <input
                        type="number"
                        value={Math.round(selectedElement.width)}
                        onChange={(e) => updateElementProperty(selectedElement.id, 'width', parseInt(e.target.value) || 1)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Height</label>
                      <input
                        type="number"
                        value={Math.round(selectedElement.height)}
                        onChange={(e) => updateElementProperty(selectedElement.id, 'height', parseInt(e.target.value) || 1)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>

                  {/* Text Properties */}
                  {selectedElement.type === 'text' && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <h4 className="font-medium text-blue-900 mb-3 flex items-center">
                          <span className="mr-2">📝</span>
                          Text Properties
                        </h4>
                        
                        <div className="space-y-4">
                          <PropertyInput
                            label="Text Content"
                            propertyName="text"
                            value={selectedElement.text || ''}
                            type="text"
                            element={selectedElement}
                            onChange={(value) => updateElementProperty(selectedElement.id, 'text', value)}
                            onUpdateDynamicProperty={updateDynamicProperty}
                            isPropertyDynamic={isPropertyDynamic}
                            getPropertyDataKey={getPropertyDataKey}
                          />
                          
                          <div className="grid grid-cols-2 gap-3">
                            <PropertyInput
                              label="Font Size"
                              propertyName="fontSize"
                              value={selectedElement.fontSize || 24}
                              type="number"
                              element={selectedElement}
                              onChange={(value) => updateElementProperty(selectedElement.id, 'fontSize', value)}
                              onUpdateDynamicProperty={updateDynamicProperty}
                              isPropertyDynamic={isPropertyDynamic}
                              getPropertyDataKey={getPropertyDataKey}
                            />
                            
                            <PropertyInput
                              label="Font Family"
                              propertyName="fontFamily"
                              value={selectedElement.fontFamily || 'Arial'}
                              type="select"
                              element={selectedElement}
                              onChange={(value) => handleFontChange(selectedElement.id, value)}
                              options={getFontOptions()}
                              isLoading={isLoadingFonts}
                              onUpdateDynamicProperty={updateDynamicProperty}
                              isPropertyDynamic={isPropertyDynamic}
                              getPropertyDataKey={getPropertyDataKey}
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <PropertyInput
                              label="Text Color"
                              propertyName="fill"
                              value={selectedElement.fill || '#000000'}
                              type="color"
                              element={selectedElement}
                              onChange={(value) => updateElementProperty(selectedElement.id, 'fill', value)}
                              onUpdateDynamicProperty={updateDynamicProperty}
                              isPropertyDynamic={isPropertyDynamic}
                              getPropertyDataKey={getPropertyDataKey}
                            />
                            
                            <PropertyInput
                              label="Text Align"
                              propertyName="textAlign"
                              value={selectedElement.textAlign || 'left'}
                              type="select"
                              element={selectedElement}
                              onChange={(value) => updateElementProperty(selectedElement.id, 'textAlign', value)}
                              options={[
                                { value: 'left', label: '⬅️ Left' },
                                { value: 'center', label: '↔️ Center' },
                                { value: 'right', label: '➡️ Right' }
                              ]}
                              onUpdateDynamicProperty={updateDynamicProperty}
                              isPropertyDynamic={isPropertyDynamic}
                              getPropertyDataKey={getPropertyDataKey}
                            />
                          </div>
                          
                          <div className="grid grid-cols-1 gap-3">
                            <PropertyInput
                              label="Vertical Align"
                              propertyName="verticalAlign"
                              value={selectedElement.verticalAlign || 'top'}
                              type="select"
                              element={selectedElement}
                              onChange={(value) => updateElementProperty(selectedElement.id, 'verticalAlign', value)}
                              options={[
                                { value: 'top', label: '⬆️ Top' },
                                { value: 'middle', label: '↕️ Middle' },
                                { value: 'bottom', label: '⬇️ Bottom' }
                              ]}
                              onUpdateDynamicProperty={updateDynamicProperty}
                              isPropertyDynamic={isPropertyDynamic}
                              getPropertyDataKey={getPropertyDataKey}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Image Properties */}
                  {selectedElement.type === 'image' && (
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <h4 className="font-medium text-green-900 mb-3 flex items-center">
                          <span className="mr-2">🖼️</span>
                          Image Properties
                        </h4>
                        
                        <div className="space-y-4">
                          <PropertyInput
                            label="Image URL"
                            propertyName="imageUrl"
                            value={selectedElement.imageUrl || ''}
                            type="text"
                            element={selectedElement}
                            onChange={(value) => updateElementProperty(selectedElement.id, 'imageUrl', value)}
                            onUpdateDynamicProperty={updateDynamicProperty}
                            isPropertyDynamic={isPropertyDynamic}
                            getPropertyDataKey={getPropertyDataKey}
                          />
                          
                          <PropertyInput
                            label="Opacity"
                            propertyName="opacity"
                            value={selectedElement.opacity || 1}
                            type="range"
                            element={selectedElement}
                            onChange={(value) => updateElementProperty(selectedElement.id, 'opacity', value)}
                            onUpdateDynamicProperty={updateDynamicProperty}
                            isPropertyDynamic={isPropertyDynamic}
                            getPropertyDataKey={getPropertyDataKey}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rectangle Properties */}
                  {selectedElement.type === 'rectangle' && (
                    <div className="space-y-4">
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <h4 className="font-medium text-purple-900 mb-3 flex items-center">
                          <span className="mr-2">⬜</span>
                          Rectangle Properties
                        </h4>
                        
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-3">
                            <PropertyInput
                              label="Fill Color"
                              propertyName="fill"
                              value={selectedElement.fill || '#3b82f6'}
                              type="color"
                              element={selectedElement}
                              onChange={(value) => updateElementProperty(selectedElement.id, 'fill', value)}
                              onUpdateDynamicProperty={updateDynamicProperty}
                              isPropertyDynamic={isPropertyDynamic}
                              getPropertyDataKey={getPropertyDataKey}
                            />
                            
                            <PropertyInput
                              label="Stroke Color"
                              propertyName="stroke"
                              value={selectedElement.stroke || '#1e40af'}
                              type="color"
                              element={selectedElement}
                              onChange={(value) => updateElementProperty(selectedElement.id, 'stroke', value)}
                              onUpdateDynamicProperty={updateDynamicProperty}
                              isPropertyDynamic={isPropertyDynamic}
                              getPropertyDataKey={getPropertyDataKey}
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <PropertyInput
                              label="Stroke Width"
                              propertyName="strokeWidth"
                              value={selectedElement.strokeWidth || 2}
                              type="number"
                              element={selectedElement}
                              onChange={(value) => updateElementProperty(selectedElement.id, 'strokeWidth', value)}
                              onUpdateDynamicProperty={updateDynamicProperty}
                              isPropertyDynamic={isPropertyDynamic}
                              getPropertyDataKey={getPropertyDataKey}
                            />
                            
                            <PropertyInput
                              label="Corner Radius"
                              propertyName="cornerRadius"
                              value={selectedElement.cornerRadius || 8}
                              type="number"
                              element={selectedElement}
                              onChange={(value) => updateElementProperty(selectedElement.id, 'cornerRadius', value)}
                              onUpdateDynamicProperty={updateDynamicProperty}
                              isPropertyDynamic={isPropertyDynamic}
                              getPropertyDataKey={getPropertyDataKey}
                            />
                          </div>
                          
                          <PropertyInput
                            label="Opacity"
                            propertyName="opacity"
                            value={selectedElement.opacity || 1}
                            type="range"
                            element={selectedElement}
                            onChange={(value) => updateElementProperty(selectedElement.id, 'opacity', value)}
                            onUpdateDynamicProperty={updateDynamicProperty}
                            isPropertyDynamic={isPropertyDynamic}
                            getPropertyDataKey={getPropertyDataKey}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Circle Properties */}
                  {selectedElement.type === 'circle' && (
                    <div className="space-y-4">
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <h4 className="font-medium text-orange-900 mb-3 flex items-center">
                          <span className="mr-2">⭕</span>
                          Circle Properties
                        </h4>
                        
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-3">
                            <PropertyInput
                              label="Fill Color"
                              propertyName="fill"
                              value={selectedElement.fill || '#10b981'}
                              type="color"
                              element={selectedElement}
                              onChange={(value) => updateElementProperty(selectedElement.id, 'fill', value)}
                              onUpdateDynamicProperty={updateDynamicProperty}
                              isPropertyDynamic={isPropertyDynamic}
                              getPropertyDataKey={getPropertyDataKey}
                            />
                            
                            <PropertyInput
                              label="Stroke Color"
                              propertyName="stroke"
                              value={selectedElement.stroke || '#059669'}
                              type="color"
                              element={selectedElement}
                              onChange={(value) => updateElementProperty(selectedElement.id, 'stroke', value)}
                              onUpdateDynamicProperty={updateDynamicProperty}
                              isPropertyDynamic={isPropertyDynamic}
                              getPropertyDataKey={getPropertyDataKey}
                            />
                          </div>
                          
                          <PropertyInput
                            label="Stroke Width"
                            propertyName="strokeWidth"
                            value={selectedElement.strokeWidth || 2}
                            type="number"
                            element={selectedElement}
                            onChange={(value) => updateElementProperty(selectedElement.id, 'strokeWidth', value)}
                            onUpdateDynamicProperty={updateDynamicProperty}
                            isPropertyDynamic={isPropertyDynamic}
                            getPropertyDataKey={getPropertyDataKey}
                          />
                          
                          <PropertyInput
                            label="Opacity"
                            propertyName="opacity"
                            value={selectedElement.opacity || 1}
                            type="range"
                            element={selectedElement}
                            onChange={(value) => updateElementProperty(selectedElement.id, 'opacity', value)}
                            onUpdateDynamicProperty={updateDynamicProperty}
                            isPropertyDynamic={isPropertyDynamic}
                            getPropertyDataKey={getPropertyDataKey}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Dynamic Summary */}
                  {Object.keys(selectedElement.dynamicProps || {}).length > 0 && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <h4 className="font-medium text-purple-900 mb-2 flex items-center">
                        <span className="mr-2">🔗</span>
                        Dynamic Properties Summary
                      </h4>
                      <div className="space-y-1">
                        {Object.entries(selectedElement.dynamicProps || {}).map(([prop, key]) => (
                          <div key={prop} className="text-sm text-purple-700 bg-purple-100 px-2 py-1 rounded">
                            <span className="font-medium">{prop}</span> → <span className="font-mono">{key}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Lock/Unlock and Delete buttons */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => selectedId && toggleLock(selectedId)}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                        selectedElement?.locked 
                          ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
                          : 'bg-gray-600 hover:bg-gray-700 text-white'
                      }`}
                      title="Lock/Unlock (Ctrl+L)"
                    >
                      {selectedElement?.locked ? '🔓 Unlock' : '🔒 Lock'}
                    </button>
                    <button
                      onClick={deleteSelected}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">👆</div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Lütfen Element Seçin</h4>
                  <p className="text-gray-600 text-sm mb-6">
                    Canvas'tan bir element seçerek özelliklerini düzenleyebilirsiniz
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h5 className="font-medium text-blue-900 mb-2">💡 İpucu</h5>
                    <ul className="text-sm text-blue-800 text-left space-y-1">
                      <li>• Sol panelden elementleri sürükleyip bırakın</li>
                      <li>• Canvas'taki elementlere tıklayarak seçin</li>
                      <li>• Klavye kısayollarını kullanın (Ctrl+C, Ctrl+V)</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* API Usage Popup */}
      {showApiPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                  <span>🔗</span>
                  <span>API Usage Guide</span>
                </h2>
                <button
                  onClick={() => setShowApiPopup(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              {/* Template Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">Template Information</h3>
                <div className="text-sm text-blue-800">
                  <p><strong>Template ID:</strong> {templateId || 'Save template to get ID'}</p>
                  <p><strong>Template Name:</strong> {templateName}</p>
                  <p><strong>Dynamic Variables:</strong> {getDynamicVariables().length > 0 ? getDynamicVariables().join(', ') : 'None'}</p>
                </div>
              </div>

              {/* Dynamic Variables Test */}
              {getDynamicVariables().length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-green-900 mb-3">🧪 Test with Dynamic Data</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {getDynamicVariables().map((variable) => (
                      <div key={variable}>
                        <label className="block text-sm font-medium text-green-800 mb-1">
                          {variable}
                        </label>
                        <input
                          type="text"
                          value={testInputs[variable] || ''}
                          onChange={(e) => setTestInputs(prev => ({ ...prev, [variable]: e.target.value }))}
                          placeholder={`Enter value for ${variable}`}
                          className="w-full px-3 py-2 border border-green-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => testApi('')}
                      disabled={isTestingApi || !templateId}
                      className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      {isTestingApi ? '🔄 Testing PNG...' : '🖼️ Test PNG'}
                    </button>
                    <button
                      onClick={() => testApi('/base64')}
                      disabled={isTestingApi || !templateId}
                      className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      {isTestingApi ? '🔄 Testing Base64...' : '📝 Test Base64'}
                    </button>
                  </div>
                  
                  {testResult && (
                    <div className="mt-3 p-3 bg-gray-100 rounded-lg text-sm text-gray-800">
                      <strong>Test Result:</strong> {testResult}
                    </div>
                  )}
                </div>
              )}

              {/* API Endpoints */}
              <div className="space-y-6">
                {/* PNG Endpoint */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <span>🖼️</span>
                    <span>Render as PNG</span>
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Returns the rendered template as a PNG image file.
                  </p>
                  
                  <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono overflow-x-auto mb-3">
                    <pre>{generateCurlCommand('')}</pre>
                  </div>
                  
                  <button
                    onClick={() => copyToClipboard(generateCurlCommand(''))}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm transition-colors"
                  >
                    📋 Copy cURL
                  </button>
                </div>

                {/* Base64 Endpoint */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <span>📝</span>
                    <span>Render as Base64</span>
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Returns the rendered template as a Base64 encoded string in JSON format.
                  </p>
                  
                  <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono overflow-x-auto mb-3">
                    <pre>{generateCurlCommand('/base64')}</pre>
                  </div>
                  
                  <button
                    onClick={() => copyToClipboard(generateCurlCommand('/base64'))}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm transition-colors"
                  >
                    📋 Copy cURL
                  </button>
                </div>

                {/* Preview Endpoint */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <span>👁️</span>
                    <span>Public Preview (No Auth)</span>
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Returns a preview of public templates without authentication.
                  </p>
                  
                  <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono overflow-x-auto mb-3">
                    <pre>{`curl -X GET "https://api-postify.eymel.co/api/render/preview/${templateId || 'TEMPLATE_ID'}" \\
  --output preview.png`}</pre>
                  </div>
                  
                  <button
                    onClick={() => copyToClipboard(`curl -X GET "https://api-postify.eymel.co/api/render/preview/${templateId || 'TEMPLATE_ID'}" \\
  --output preview.png`)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm transition-colors"
                  >
                    📋 Copy cURL
                  </button>
                </div>
              </div>

              {/* Authentication Info */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                <h3 className="font-semibold text-yellow-900 mb-2">🔐 Authentication</h3>
                <div className="text-sm text-yellow-800">
                  <p className="mb-2">To use the API, you need to include your API key in the request headers:</p>
                  <div className="bg-yellow-100 p-3 rounded-lg mb-3">
                    <div className="flex items-center justify-between">
                      <code className="text-xs font-mono">x-api-key: {apiKey || 'YOUR_API_KEY'}</code>
                      <button
                        onClick={() => copyToClipboard(apiKey || 'YOUR_API_KEY')}
                        className="ml-2 bg-yellow-600 hover:bg-yellow-700 text-white px-2 py-1 rounded text-xs transition-colors"
                      >
                        📋 Copy API Key
                      </button>
                    </div>
                  </div>
                  {!apiKey ? (
                    <p className="text-red-700 font-medium">⚠️ Please login to get your API key!</p>
                  ) : (
                    <p className="text-green-700">✅ Your API key is ready to use!</p>
                  )}
                </div>
              </div>

              {/* Response Examples */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-6">
                <h3 className="font-semibold text-gray-900 mb-3">📄 Response Examples</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-800 mb-2">Base64 Response:</h4>
                    <div className="bg-gray-900 text-green-400 p-3 rounded text-xs font-mono overflow-x-auto">
                      <pre>{`{
  "success": true,
  "image": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...",
  "templateId": "${templateId || 'template-id'}",
  "renderCount": 42
}`}</pre>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-800 mb-2">Error Response:</h4>
                    <div className="bg-gray-900 text-red-400 p-3 rounded text-xs font-mono overflow-x-auto">
                      <pre>{`{
  "error": "Template bulunamadı"
}`}</pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KonvaCanvasEditor;
